import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { createLearningAssignment, fetchAssignedLearning, fetchLearningCatalog } from '../../lib/assignedLearning';
import { buildLearningAssignmentSuggestions } from '../../lib/learningAssignmentSuggestions';

const typeLabels = {
  topic: 'Topic',
  resource: 'Resource',
  track: 'Track'
};

const optionDescription = (type, item) => {
  if (type === 'topic') return item.short_summary || item.content_category;
  if (type === 'resource') return item.short_description || item.resource_type;
  return item.description || item.content_category;
};

const ProfessionalAssignLearning = ({ matter, profile, onAssigned }) => {
  const [catalog, setCatalog] = useState({ topics: [], resources: [], tracks: [] });
  const [assignments, setAssignments] = useState([]);
  const [assignmentType, setAssignmentType] = useState('topic');
  const [selectedId, setSelectedId] = useState('');
  const [note, setNote] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        setLoading(true);
        setError(null);
        const [catalogData, assignmentRows] = await Promise.all([
          fetchLearningCatalog(),
          matter?.id ? fetchAssignedLearning({ matterIds: [matter.id] }) : Promise.resolve([])
        ]);
        setCatalog(catalogData);
        setAssignments(assignmentRows);
      } catch (err) {
        console.error('Learning assignment catalog error:', err);
        setError(err.message || 'Unable to load learning catalog.');
      } finally {
        setLoading(false);
      }
    };

    loadCatalog();
  }, [matter?.id]);

  const options = useMemo(() => {
    if (assignmentType === 'topic') return catalog.topics;
    if (assignmentType === 'resource') return catalog.resources;
    return catalog.tracks;
  }, [assignmentType, catalog]);

  const selectedItem = options.find((item) => item.id === selectedId);
  const suggestions = useMemo(() => buildLearningAssignmentSuggestions({
    matter,
    catalog,
    assignments
  }), [matter, catalog, assignments]);

  const submitAssignment = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setNotice(null);

      const assignment = await createLearningAssignment({
        matter,
        assignedByUserId: profile?.id,
        assignmentType,
        itemId: selectedId,
        note,
        dueDate
      });

      setNotice(`${typeLabels[assignmentType]} assigned to the client.`);
      setAssignments((current) => [assignment, ...current]);
      setSelectedId('');
      setNote('');
      setDueDate('');
      onAssigned?.(assignment);
    } catch (err) {
      console.error('Create learning assignment error:', err);
      setError(
        err.code === '23505'
          ? 'That learning item is already assigned for this plan.'
          : err.message || 'Unable to assign learning.'
      );
    } finally {
      setSaving(false);
    }
  };

  const applySuggestion = (suggestion) => {
    setAssignmentType(suggestion.assignmentType);
    setSelectedId(suggestion.item.id);
    setNote(suggestion.note || '');
    setNotice(null);
    setError(null);
  };

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
        <h3 className="flex items-center text-sm font-bold uppercase tracking-tight text-slate-800">
          <SafeIcon icon={FiIcons.FiSend} className="mr-2 h-4 w-4 text-slate-400" />
          Assign Learning
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Assign a topic, resource, or short learning track for this client to review.
        </p>
      </div>

      <form onSubmit={submitAssignment} className="space-y-4 p-6">
        {error && (
          <div className="rounded-md border-l-4 border-red-400 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {notice && (
          <div className="rounded-md border-l-4 border-green-400 bg-green-50 p-3 text-sm text-green-700">
            {notice}
          </div>
        )}

        {assignments.length > 0 && (
          <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-4">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Currently Assigned
            </p>
            <div className="space-y-2">
              {assignments.slice(0, 5).map((assignment) => {
                const title = assignment.topic?.title || assignment.resource?.title || assignment.track?.title || 'Assigned Learning';
                return (
                  <div key={assignment.id} className="flex flex-col gap-1 rounded-md border border-slate-200 bg-white px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{title}</p>
                      <p className="text-xs text-slate-500">
                        {typeLabels[assignment.assignment_type]} assigned
                        {assignment.assignment_type === 'track' && assignment.total_item_count > 0
                          ? ` - ${assignment.completed_item_count} of ${assignment.total_item_count} complete`
                          : ''}
                      </p>
                    </div>
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      {assignment.status.replaceAll('_', ' ')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {suggestions.length > 0 && (
          <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-blue-700">
                Suggested Learning
              </p>
              <span className="text-xs text-blue-600">Based on plan details</span>
            </div>
            <div className="space-y-3">
              {suggestions.map((suggestion) => (
                <div key={suggestion.id} className="rounded-md border border-blue-100 bg-white p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span className="rounded bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-blue-700">
                          {typeLabels[suggestion.assignmentType]}
                        </span>
                        <p className="text-sm font-bold text-slate-900">{suggestion.item.title}</p>
                      </div>
                      <p className="text-xs leading-relaxed text-slate-600">{suggestion.reason}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => applySuggestion(suggestion)}
                      className="inline-flex items-center justify-center rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-blue-700 hover:bg-blue-100"
                    >
                      <SafeIcon icon={FiIcons.FiPlus} className="mr-2 h-3.5 w-3.5" />
                      Use
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {Object.entries(typeLabels).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                setAssignmentType(value);
                setSelectedId('');
              }}
              className={`rounded-md border px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                assignmentType === value
                  ? 'border-blue-200 bg-blue-50 text-blue-700'
                  : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            <SafeIcon icon={FiIcons.FiLoader} className="mr-2 h-4 w-4 animate-spin text-slate-300" />
            Loading learning catalog...
          </div>
        ) : (
          <>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Learning {typeLabels[assignmentType]}
              </label>
              <select
                value={selectedId}
                onChange={(event) => setSelectedId(event.target.value)}
                className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-slate-500"
                required
              >
                <option value="">Choose {typeLabels[assignmentType].toLowerCase()}</option>
                {options.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </select>
              {selectedItem && (
                <p className="mt-2 rounded-md border border-slate-100 bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">
                  {optionDescription(assignmentType, selectedItem)}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Note to Client
              </label>
              <textarea
                rows={3}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-slate-500"
                placeholder="Optional context for why this is helpful..."
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  Due Date
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(event) => setDueDate(event.target.value)}
                  className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-slate-500"
                />
              </div>
              <button
                type="submit"
                disabled={saving || !selectedId}
                className="inline-flex items-center justify-center rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
              >
                <SafeIcon icon={saving ? FiIcons.FiLoader : FiIcons.FiSend} className={`mr-2 h-4 w-4 ${saving ? 'animate-spin' : ''}`} />
                Assign
              </button>
            </div>
          </>
        )}
      </form>
    </section>
  );
};

export default ProfessionalAssignLearning;
